import { useMemo, useState } from "react";
import { Button, Input, Progress, Table } from "reactstrap";
import cx from "classnames";
import PageContainer from "@components/layout/PageContainer";
import LoadingScreen from "@components/content/LoadingScreen";
import ConfirmModal from "@components/content/ConfirmModal";
import SearchCabysOffCanvas from "@components/layout/Offcanvas/SearchCabysOffCanvas";
import { CabysAuditApi, CabysFix, CabysFixProgress } from "@/api/CabysAuditApi";
import { ProductDataType } from "@/api/ProductsApi";
import {
    CABYS_AUDIT_STATUS_LABELS,
    CABYS_AUDIT_STATUS_MESSAGES,
    CabysAuditRow,
    CabysAuditTarget,
    validateCabysFix,
} from "@utils/cabysAudit";
import { CABYS_CODE_LENGTH, normalizeCabysCode } from "@utils/Cabys";
import { PRODUCT_TYPES } from "@utils/Constants";
import { OpenCabysSearchPubSub } from "@utils/pubsubs/PubSub";
import { showAlert } from "@utils/Alert";
import styles from "./style.module.scss";

type CabysAuditProps = {
    target: CabysAuditTarget;
};

/** Lo que el usuario propone guardar para una fila. */
type CabysDraft = {
    codigo_cabys: string;
    tipo_producto: string;
};

const COPY = {
    product: {
        title: "Verificar CAByS de Productos",
        entity: "producto",
        entityPlural: "productos",
        codeHeader: "Codigo",
        emptyRow: "No hay productos con el CAByS mal configurado",
        emptyScan: "No se encontraron productos con el CAByS mal configurado",
    },
    category: {
        title: "Verificar CAByS de Categorias",
        entity: "categoria",
        entityPlural: "categorias",
        codeHeader: "Codigo",
        emptyRow: "No hay categorias con el CAByS mal configurado",
        emptyScan: "No se encontraron categorias con el CAByS mal configurado",
    },
} as const;

const PRODUCT_TYPE_OPTIONS = Object.entries(PRODUCT_TYPES);

/**
 * Correccion propuesta por omision: alinear el tipo al CAByS que ya tiene el registro.
 *
 * Es la que resuelve los casos `mismatch` y `missing_type` sin que el usuario escriba
 * nada. Cuando el CAByS es el que esta mal, el usuario lo reemplaza en el input.
 */
const suggestDraft = (row: CabysAuditRow): CabysDraft => ({
    codigo_cabys: row.codigoCabys,
    tipo_producto: `${row.expectedProductType}`,
});

const CabysAudit = (props: CabysAuditProps) => {
    const { target } = props;
    const copy = COPY[target];

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasScanned, setHasScanned] = useState<boolean>(false);
    const [rows, setRows] = useState<CabysAuditRow[]>([]);
    const [drafts, setDrafts] = useState<Record<string, CabysDraft>>({});
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
    const [progress, setProgress] = useState<CabysFixProgress>({ processed: 0, total: 0 });
    const [activeRowId, setActiveRowId] = useState<string>();

    const validationByRow = useMemo(() => {
        return rows.reduce<Record<string, ReturnType<typeof validateCabysFix>>>((acc, row) => {
            const draft = drafts[row.id];
            acc[row.id] = validateCabysFix(draft?.codigo_cabys, draft?.tipo_producto);
            return acc;
        }, {});
    }, [rows, drafts]);

    const fixableIds = useMemo(
        () => rows.filter((row) => validationByRow[row.id]?.ok).map((row) => row.id),
        [rows, validationByRow]
    );

    const pendingFixes = useMemo<CabysFix[]>(() => {
        return rows
            .filter((row) => selectedIds.includes(row.id) && validationByRow[row.id]?.ok)
            .map((row) => ({
                id: row.id,
                codigo_cabys: normalizeCabysCode(drafts[row.id]?.codigo_cabys),
                tipo_producto: Number(drafts[row.id]?.tipo_producto),
            }));
    }, [rows, selectedIds, validationByRow, drafts]);

    const scan = async () => {
        setIsLoading(true);
        try {
            const found = target === "category"
                ? await CabysAuditApi.scanCategoryCabys()
                : await CabysAuditApi.scanProductCabys();

            const suggested = found.reduce<Record<string, CabysDraft>>((acc, row) => {
                acc[row.id] = suggestDraft(row);
                return acc;
            }, {});

            setRows(found);
            setDrafts(suggested);
            setSelectedIds(found.filter((row) => validateCabysFix(suggested[row.id].codigo_cabys, suggested[row.id].tipo_producto).ok).map((row) => row.id));
            setHasScanned(true);

            if (found.length === 0) {
                showAlert("success", copy.emptyScan);
            }
        } catch (err) {
            const message = err instanceof Error ? err.message : `No fue posible revisar los ${copy.entityPlural}`;
            showAlert("danger", message);
        } finally {
            setIsLoading(false);
        }
    };

    const setDraftField = (id: string, field: keyof CabysDraft, value: string) => {
        setDrafts((current) => ({
            ...current,
            [id]: { ...current[id], [field]: value },
        }));
    };

    const toggleSelection = (id: string, checked: boolean) => {
        setSelectedIds((current) => {
            if (checked) return current.includes(id) ? current : [...current, id];
            return current.filter((selected) => selected !== id);
        });
    };

    const toggleSelectAll = (checked: boolean) => {
        setSelectedIds(checked ? fixableIds : []);
    };

    const openCabysSearch = (id: string) => {
        setActiveRowId(id);
        OpenCabysSearchPubSub.publish({ openOffCanvas: true });
    };

    /**
     * El offcanvas devuelve el CAByS con su tipo ya resuelto, asi que llena las dos
     * columnas de la fila que abrio la busqueda.
     */
    const onCabysSelected = (productData?: ProductDataType) => {
        if (!activeRowId || !productData?.codigo_cabys) return;

        setDrafts((current) => ({
            ...current,
            [activeRowId]: {
                codigo_cabys: `${productData.codigo_cabys}`,
                tipo_producto: `${productData.tipo_producto ?? ""}`,
            },
        }));
        setActiveRowId(undefined);
    };

    const runFixes = async () => {
        setIsConfirmOpen(false);

        if (pendingFixes.length === 0) {
            showAlert("danger", "No hay correcciones validas para guardar");
            return;
        }

        setIsLoading(true);
        setProgress({ processed: 0, total: pendingFixes.length });
        try {
            const summary = await CabysAuditApi.applyCabysFixes(target, pendingFixes, setProgress);
            const message = `Actualizados: ${summary.updated.length}. Fallidos: ${summary.failed.length}`;
            showAlert(summary.failed.length > 0 ? "warning" : "success", message);

            await scan();
        } catch (err) {
            const message = err instanceof Error ? err.message : "No fue posible guardar las correcciones";
            showAlert("danger", message);
        } finally {
            setProgress({ processed: 0, total: 0 });
            setIsLoading(false);
        }
    };

    const renderRow = (row: CabysAuditRow) => {
        const draft = drafts[row.id] ?? suggestDraft(row);
        const validation = validationByRow[row.id];
        const detail = validation?.ok ? CABYS_AUDIT_STATUS_MESSAGES[row.status] : validation?.message;

        return (
            <tr key={row.id}>
                <td>
                    <Input
                        type="checkbox"
                        data-testid={`cabys-select-${row.id}`}
                        checked={selectedIds.includes(row.id)}
                        disabled={!validation?.ok}
                        onChange={(event) => toggleSelection(row.id, event.target.checked)}
                        aria-label={`Seleccionar ${copy.entity} ${row.codigo}`}
                    />
                </td>
                <td>{row.codigo || "-"}</td>
                <td>{row.descripcion || "-"}</td>
                <td>{row.productType ? PRODUCT_TYPES[row.productType] : "-"}</td>
                <td>{row.codigoCabys || "-"}</td>
                <td>
                    <Input
                        type="select"
                        data-testid={`cabys-type-${row.id}`}
                        value={draft.tipo_producto}
                        onChange={(event) => setDraftField(row.id, "tipo_producto", event.target.value)}
                        aria-label={`Tipo de producto corregido de ${row.codigo}`}
                    >
                        <option value="">Seleccione</option>
                        {PRODUCT_TYPE_OPTIONS.map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </Input>
                </td>
                <td>
                    <div className={styles.cabysCell}>
                        <Input
                            type="text"
                            data-testid={`cabys-code-${row.id}`}
                            maxLength={CABYS_CODE_LENGTH}
                            value={draft.codigo_cabys}
                            onChange={(event) => setDraftField(row.id, "codigo_cabys", event.target.value)}
                            aria-label={`CAByS corregido de ${row.codigo}`}
                        />
                        <Button
                            type="button"
                            color="primary"
                            data-testid={`cabys-search-${row.id}`}
                            onClick={() => openCabysSearch(row.id)}
                        >
                            Buscar
                        </Button>
                    </div>
                </td>
                <td>
                    <span className={cx(styles.statusBadge, styles[`status_${row.status}`])}>
                        {CABYS_AUDIT_STATUS_LABELS[row.status]}
                    </span>
                </td>
                <td>{detail}</td>
            </tr>
        );
    };

    return (
        <PageContainer title={copy.title} backUrl="/user/products/inventory-management">
            <p className="mb-4">
                Esta herramienta revisa el inventario buscando registros cuyo CAByS no concuerda con el
                tipo de producto: Hacienda clasifica como servicio todo CAByS que inicia en 6, 7, 8 o 9.
                Solo se corrige la ficha del {copy.entity}; los documentos ya emitidos conservan el CAByS
                con el que se facturaron.
            </p>

            <div className={styles.actions}>
                <Button color="primary" onClick={scan} disabled={isLoading} data-testid="cabys-scan">
                    Escanear
                </Button>
                <Button
                    color="success"
                    disabled={isLoading || pendingFixes.length === 0}
                    onClick={() => setIsConfirmOpen(true)}
                    data-testid="cabys-save"
                >
                    Guardar cambios ({pendingFixes.length})
                </Button>
            </div>

            {progress.total > 0 && (
                <Progress className="mt-3" value={progress.processed} max={progress.total} data-testid="cabys-progress">
                    {progress.processed} / {progress.total}
                </Progress>
            )}

            {hasScanned && (
                <div className="table-responsive mt-4">
                    <Table hover bordered className={styles.table}>
                        <thead>
                            <tr>
                                <th>
                                    <Input
                                        type="checkbox"
                                        data-testid="cabys-select-all"
                                        checked={fixableIds.length > 0 && selectedIds.length === fixableIds.length}
                                        onChange={(event) => toggleSelectAll(event.target.checked)}
                                        aria-label="Seleccionar todas las correcciones validas"
                                    />
                                </th>
                                <th>{copy.codeHeader}</th>
                                <th>Descripcion</th>
                                <th>Tipo actual</th>
                                <th>CAByS actual</th>
                                <th>Tipo corregido</th>
                                <th>CAByS corregido</th>
                                <th>Estado</th>
                                <th>Detalle</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length === 0 && (
                                <tr>
                                    <td colSpan={9} className="text-center">{copy.emptyRow}</td>
                                </tr>
                            )}
                            {rows.map(renderRow)}
                        </tbody>
                    </Table>
                </div>
            )}

            <ConfirmModal
                isOpen={isConfirmOpen}
                toggle={() => setIsConfirmOpen(false)}
                title="Confirmar correccion"
                primaryBtn="Guardar"
                primaryBtnColor="success"
                secondaryBtn="Cancelar"
                secondaryBtnColor="secondary"
                onConfirm={runFixes}
                onCancel={() => setIsConfirmOpen(false)}
            >
                Se actualizara el CAByS y el tipo de {pendingFixes.length} {copy.entityPlural}. Los documentos
                ya emitidos no se modifican.
            </ConfirmModal>

            <SearchCabysOffCanvas onCabysSelected={onCabysSelected} />

            <LoadingScreen isLoading={isLoading} />
        </PageContainer>
    );
};

export default CabysAudit;
